import type { KeyboardEvent, RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

type UsePopoverFocusOptions = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement>;
  popoverRef: RefObject<HTMLElement>;
  /** Closes the popover — the host's own close handler. */
  onClose: () => void;
  /**
   * Whether the trigger was activated from the keyboard (a click event with
   * `detail === 0`). Only then does focus move to `initialFocusSelector`;
   * a pointer/touch open focuses the popover container itself (which needs
   * `tabIndex={-1}`), so a touch device's on-screen keyboard doesn't pop up,
   * shrink the viewport and clamp the popover, while Escape still works.
   */
  openedFromKeyboard: boolean;
  /** Element inside the popover to focus on a keyboard open; defaults to the first focusable one. */
  initialFocusSelector?: string;
};

/**
 * Keyboard access for a popover portalled to the end of `document.body`,
 * where tab order would otherwise never reach it: focuses into it on open,
 * closes on Escape, and returns focus to the trigger on close.
 *
 * Focus is returned only when the close was a deliberate in-popover action
 * (Escape, Apply/Clear, the trigger — see `restoreFocusOnClose`) or an
 * outside click on something non-focusable while focus was inside. An
 * outside click on another focusable element keeps focus there, and a close
 * because the trigger scrolled out of view doesn't yank the page back to it.
 */
export function usePopoverFocus({
  isOpen,
  triggerRef,
  popoverRef,
  onClose,
  openedFromKeyboard,
  initialFocusSelector = FOCUSABLE_SELECTOR,
}: UsePopoverFocusOptions) {
  const restoreFocusRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      restoreFocusRef.current = false;
      const initial = openedFromKeyboard
        ? popoverRef.current?.querySelector<HTMLElement>(initialFocusSelector)
        : popoverRef.current;
      initial?.focus({ preventScroll: true });

      const handlePointerDown = (event: Event) => {
        const popover = popoverRef.current;
        const target = event.target;
        if (
          !popover ||
          !(target instanceof Element) ||
          popover.contains(target) ||
          triggerRef.current?.contains(target)
        ) {
          return;
        }
        restoreFocusRef.current =
          !target.closest(FOCUSABLE_SELECTOR) &&
          popover.contains(document.activeElement);
      };
      // Capture phase, so this runs before the host's outside-click close.
      document.addEventListener("mousedown", handlePointerDown, true);
      document.addEventListener("touchstart", handlePointerDown, true);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown, true);
        document.removeEventListener("touchstart", handlePointerDown, true);
      };
    }

    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      if (restoreFocusRef.current) triggerRef.current?.focus();
      restoreFocusRef.current = false;
    }
  }, [
    isOpen,
    openedFromKeyboard,
    initialFocusSelector,
    popoverRef,
    triggerRef,
  ]);

  /** Call right before a deliberate close (Apply/Clear/trigger) to return focus to the trigger. */
  const restoreFocusOnClose = useCallback(() => {
    restoreFocusRef.current = true;
  }, []);

  const onPopoverKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      restoreFocusRef.current = true;
      onClose();
    },
    [onClose]
  );

  return { restoreFocusOnClose, onPopoverKeyDown };
}
